/**
 * Turning a row of `Learner.sessions` into the two lines a parent reads.
 *
 * Kept dependency-free from Relay's generated types for the same reason
 * `debrief.ts` is: this is the testable seam, and a plain `node --test` run
 * has no loader for `.tsx` and no Relay runtime.
 */
import { formatDate } from '../i18n/format.ts';
import { t } from '../i18n/t.ts';
import { KID_GAMES } from './topics.english.ts';
import { topicLabel } from './topics.ts';

export type PastSession = {
  id: string;
  topic?: string | null;
  status: string;
  startedAt: string;
  character?: { displayName: string } | null;
};

export type SessionRow = {
  id: string;
  /** "Today", "Yesterday", or a date. */
  when: string;
  /** What it was about, and who with. */
  what: string;
  /**
   * Whether there is anything to open.
   *
   * True for everything that is not still running — including a session whose
   * debrief failed or was never written. Which of those it is belongs to
   * `debriefState`, which the opened view already asks; deciding it twice in
   * two places is how the two answers start disagreeing.
   */
  readable: boolean;
};

/**
 * Local midnight, as a timestamp.
 *
 * Exported for `progress.ts`'s streak, which has to agree with `whenLabel`
 * about where a day begins. Two definitions of "a day" in one screen is how
 * a row labelled "Yesterday" ends up breaking a streak that says it did not.
 */
export function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const DAY_MS = 86_400_000;

/**
 * Local calendar days, not elapsed hours: a conversation at 23:00 and one at
 * 01:00 the next morning are two different days to the person who had them,
 * however few hours apart they are.
 */
export function whenLabel(startedAt: string, now: Date): string {
  const then = new Date(startedAt);
  if (Number.isNaN(then.getTime())) return '';
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY_MS);
  if (days === 0) return t('session-today');
  if (days === 1) return t('session-yesterday');
  return formatDate(then, { day: 'numeric', month: 'short' });
}

export function describeSession(s: PastSession, now: Date): SessionRow {
  // A topic is a curated string from `TOPICS`; free chat has none. Named
  // rather than left blank, because a row with no subject reads as a bug.
  const who = s.character?.displayName;
  const subject = s.topic != null ? topicLabel(s.topic, who) : t('session-free-chat');
  return {
    id: s.id,
    when: whenLabel(s.startedAt, now),
    // Teach's label already names who was taught ("Teach Cappy"), so it is
    // not followed by "with Cappy" too (kid play design §8.8).
    what: who && s.topic !== KID_GAMES[2] ? t('session-row-with', { subject, who }) : subject,
    readable: s.status !== 'ACTIVE',
  };
}
